import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, X, ArrowRight, Loader2, Zap } from "lucide-react";
import {
  isPushSupported, currentPushPermission,
  subscribeToPush, isCurrentlySubscribed, sendTestPush,
} from "@/lib/web-push";
import { useAuth } from "@/lib/auth";

const DISMISS_KEY_PREFIX = "batisur_push_onboarding_dismissed_";
const DISMISS_DAYS = 7;

/**
 * Contextual onboarding banner suggesting Web Push activation.
 *
 * Shown to authenticated users on their Dashboard when:
 *  - Push is technically supported
 *  - User hasn't already granted permission
 *  - User hasn't dismissed the banner in the last 7 days
 *
 * Clicking "Activer maintenant" triggers the browser permission popup
 * + registers the subscription with the backend.
 */
export default function PushOnboardingBanner() {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user || !isPushSupported()) return;
    // Skip if already granted or explicitly denied
    const perm = currentPushPermission();
    if (perm === "denied") return;
    isCurrentlySubscribed().then((sub) => {
      if (sub) return; // Already subscribed
      // Check dismissal timestamp
      try {
        const raw = localStorage.getItem(DISMISS_KEY_PREFIX + user.id);
        if (raw) {
          const ts = parseInt(raw, 10);
          if (ts && Date.now() - ts < DISMISS_DAYS * 86400_000) return;
        }
      } catch (_e) { /* localStorage unavailable */ }
      // Show after a small delay to let the page settle
      const t = setTimeout(() => setVisible(true), 2500);
      return () => clearTimeout(t);
    });
  }, [user]);

  const activate = async () => {
    setBusy(true);
    try {
      await subscribeToPush();
      toast.success("Notifications activées ! Vous ne raterez plus rien.");
      try { await sendTestPush(); } catch (_e) { /* ignore */ }
      setVisible(false);
    } catch (err) {
      toast.error(err?.message || "Impossible d'activer les notifications.");
    } finally {
      setBusy(false);
    }
  };

  const dismiss = () => {
    try {
      if (user) localStorage.setItem(DISMISS_KEY_PREFIX + user.id, String(Date.now()));
    } catch (_e) { /* localStorage unavailable */ }
    setVisible(false);
  };

  if (!visible) return null;

  const role = user?.role;
  const label = role === "artisan"
    ? "Pour ne rater aucun devis à accepter ni aucun message client."
    : "Pour ne rater aucun devis ou message d'artisan.";

  return (
    <div
      className="fixed bottom-4 right-4 z-40 max-w-sm w-[92vw] sm:w-96 border-2 border-[#0A0A0B] bg-white shadow-[6px_6px_0_#FF4D00] animate-in slide-in-from-bottom-4"
      data-testid="push-onboarding-banner"
      role="dialog"
      aria-label="Activer les notifications"
    >
      <div className="flex items-start gap-3 p-4">
        <div className="w-10 h-10 border-2 border-[#0A0A0B] bg-[#FF4D00] flex items-center justify-center shrink-0">
          <Bell className="w-5 h-5 text-white" strokeWidth={2.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="mono text-[10px] uppercase tracking-widest text-[#FF4D00] font-semibold flex items-center gap-1">
            <Zap className="w-3 h-3" /> // Activation notifications
          </div>
          <div className="display text-base leading-tight mt-1">
            Activez les notifications navigateur
          </div>
          <p className="text-xs text-zinc-600 mt-1 leading-snug">{label} Alertes instantanées même onglet fermé.</p>
          <div className="mt-3 flex items-center gap-2">
            <button
              onClick={activate}
              disabled={busy}
              className="mono text-[10px] uppercase tracking-widest bg-[#FF4D00] text-white border-2 border-[#0A0A0B] px-3 py-2 flex items-center gap-1 hover:bg-[#0A0A0B] transition"
              data-testid="push-onboarding-activate"
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRight className="w-3 h-3" />}
              {busy ? "Activation..." : "Activer maintenant"}
            </button>
            <button
              onClick={dismiss}
              className="mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-[#0A0A0B] px-2 py-2"
              data-testid="push-onboarding-later"
            >
              Plus tard
            </button>
          </div>
        </div>
        <button
          onClick={dismiss}
          aria-label="Fermer"
          className="text-zinc-400 hover:text-[#0A0A0B] p-1"
          data-testid="push-onboarding-close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
